<template>
    <div v-if="this.$root.user && this.$root.user.active && this.isDocumentCompositionEnabled() || (this.isNotProduction() && this.isDocumentCompositionEnabled())">
    <div class="nav-item dropdown document-composition">
        <a class="btn btn-outline-primary btn-sm" @click="this.openDocumentComposition()" href="#" role="button" aria-expanded="false">
            Document composition
        </a>
    </div>

    <ToastDialog :title="toastTitle" :message="toastMessage" :error="toastError"/>

    <ModalView :model-value="showModal" @update:model-value="showModal = $event" @close="closeModal">
        <template #header>
            <h5 class="modal-title">Documenti</h5>
        </template>
        <template #body>
            <div class="mb-4">
                <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Caricamento...</span>
                    </div>
                </div>
                <div v-else-if="documents.length === 0" class="alert alert-info" role="alert">
                    <h4 class="alert-heading">Nessun documento disponibile</h4>
                    <p>Non sono presenti documenti da visualizzare.</p>
                </div>
                <div v-else class="list-group list-group-flush">
                    <div v-for="document in documents" :key="document.id" 
                        class="list-group-item list-group-item-action document-item">
                        <div class="d-flex w-100 justify-content-between align-items-start">
                            <div class="flex-grow-1">
                                <h5 class="mb-2">
                                    <svg class="icon icon-sm me-2">
                                        <use :href="baseURL + '/bootstrap-italia/dist/svg/sprites.svg#it-file'"></use>
                                    </svg>
                                    {{ document.title }}
                                </h5>
                                <p class="mb-2 text-muted" v-if="document.description">
                                    {{ document.description }}
                                </p>
                                <div class="d-flex gap-3 small text-muted">
                                    <span v-if="document.version">
                                        <svg class="icon icon-xs">
                                            <use :href="baseURL + '/bootstrap-italia/dist/svg/sprites.svg#it-code-circle'"></use>
                                        </svg>
                                        Versione: {{ document.version.title }}
                                    </span>
                                    <span v-if="document.updated_at">
                                        <svg class="icon icon-xs">
                                            <use :href="baseURL + '/bootstrap-italia/dist/svg/sprites.svg#it-calendar'"></use>
                                        </svg>
                                        {{ formatDate(document.updated_at) }}
                                    </span>
                                    <span :class="document.active ? 'text-success' : 'text-danger'">
                                        <svg class="icon icon-xs">
                                            <use :href="baseURL + '/bootstrap-italia/dist/svg/sprites.svg#it-' + (document.active ? 'check-circle' : 'close-circle')"></use>
                                        </svg>
                                        {{ document.active ? 'Attivo' : 'Non attivo' }}
                                    </span>
                                </div>
                            </div>
                            <div class="d-flex gap-2 ms-3">
                                <button class="btn btn-primary btn-sm" 
                                        @click="selectDocument(document)"
                                        title="Seleziona documento">
                                    <svg class="icon icon-white icon-sm">
                                        <use :href="baseURL + '/bootstrap-italia/dist/svg/sprites.svg#it-plus-circle'"></use>
                                    </svg>
                                    Seleziona
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="!loading && documents.length > 0 && lastPage > 1" class="d-flex justify-content-between align-items-center mt-3">
                    <div class="small text-muted">
                        Pagina {{ currentPage }} di {{ lastPage }}
                        <span v-if="total !== null">(Totale: {{ total }})</span>
                    </div>
                    <div class="btn-group" role="group" aria-label="Paginazione documenti">
                        <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            :disabled="currentPage <= 1 || loading"
                            @click="goToPage(currentPage - 1)"
                        >
                            Precedente
                        </button>

                        <template v-for="(item, idx) in paginationItems()" :key="String(item) + '-' + idx">
                            <span v-if="item === '...'" class="btn btn-outline-primary btn-sm disabled">...</span>
                            <button
                                v-else
                                type="button"
                                class="btn btn-sm"
                                :class="Number(item) === currentPage ? 'btn-primary' : 'btn-outline-primary'"
                                :disabled="loading"
                                @click="goToPage(item)"
                            >
                                {{ item }}
                            </button>
                        </template>

                        <button
                            type="button"
                            class="btn btn-outline-primary btn-sm"
                            :disabled="currentPage >= lastPage || loading"
                            @click="goToPage(currentPage + 1)"
                        >
                            Successiva
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </ModalView>

    <ModalView :model-value="!!selectedDocument" @update:model-value="selectedDocument = $event ? selectedDocument : null" @close="closeWizardModal">
        <template #header>
            <h5 class="modal-title">{{ selectedDocument?.title || 'Documento' }}</h5>
        </template>
        <template #body>
            <div v-if="selectedDocument" class="mb-4">
                <DocumentWizardRenderer 
                ref="documentWizardRenderer"
                :wizard="selectedDocument.wizard"
                :endpoint="`${this.$root.config.VUE_APP_BACKOFFICE_URL}/documents/compile`" 
                :onSuccess="(response) => { this.showEditor(response) }" 
                :onFailed="(error) => { this.showErrorToast('Errore', 'Si è verificato un errore nella generazione del documento') }" 
                :post="this.compileDocumentPost" 
                :showKeys="false"/>
            </div>
        </template>
    </ModalView>

    <div v-if="editor">
            <ModalView v-model="showGeneratedDocumentEditor" title="Modifica Documento" icon="bi bi-play">
                <template #header>
                    <h5 class="modal-title">Modifica Documento</h5>
                </template>
                <template #body>
                <RichTextEditor :editor="editor" ref="richTextEditor" class="col-12 mb-4 px-0">
                    <div class="btn-group ms-2" role="group" aria-label="Tabelle">
                    <button
                        @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
                        class="toolbar-button"
                        :disabled="editor.isActive('table')"
                        title="Inserisci tabella (3x3)"
                    >
                        <i class="bi bi-table"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().addColumnBefore().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Aggiungi colonna prima"
                    >
                        <i class="bi bi-arrow-left-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().addColumnAfter().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Aggiungi colonna dopo"
                    >
                        <i class="bi bi-arrow-right-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().addRowBefore().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Aggiungi riga sopra"
                    >
                        <i class="bi bi-arrow-up-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().addRowAfter().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Aggiungi riga sotto"
                    >
                        <i class="bi bi-arrow-down-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().deleteColumn().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Elimina colonna"
                    >
                        <i class="bi bi-dash-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().deleteRow().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Elimina riga"
                    >
                        <i class="bi bi-dash-square"></i>
                    </button>
                    <button
                        @click="editor.chain().focus().deleteTable().run()"
                        class="toolbar-button"
                        :disabled="!editor.isActive('table')"
                        title="Elimina tabella"
                    >
                        <i class="bi bi-trash3"></i>
                    </button>
                </div>
                </RichTextEditor>
                <div class="floating-download-buttons bg-body border rounded shadow p-2 d-inline-flex align-items-center">
                    <div class="btn-group" role="group" aria-label="Download documenti">
                        <button
                            type="button"
                            class="btn btn-outline-primary"
                            title="Scarica in formato ODT"
                            @click="downloadOdt"
                        >
                            <i class="bi bi-file-earmark-text me-2"></i>Scarica ODT
                        </button>
                        <button
                            type="button"
                            class="btn btn-outline-warning"
                            title="Scarica in formato DOCX"
                            @click="downloadDocx"
                        >
                            <i class="bi bi-file-earmark-word me-2"></i>Scarica DOCX
                        </button>
                    </div>
                </div>
                </template>
            </ModalView>
        </div>
    </div>
</template>

<script>
import ModalView from '../common/ModalView.vue';
import 'digit-app-document-wizard/dist/digit-app-document-wizard-lib.css';
import { DocumentWizardRenderer } from 'digit-app-document-wizard';
import toastable from '../../mixins/toastable';
import ToastDialog from '../common/ToastDialog.vue';

import { Editor } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Heading from '@tiptap/extension-heading';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import ImageResize from 'tiptap-extension-resize-image';
import RichTextEditor from '@/components/common/RichTextEditor.vue';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';

import utils from '@/mixins/utils';

export default {
    name: "DocumentComposition",
    components: {
        ModalView,
        DocumentWizardRenderer,
        ToastDialog,
        RichTextEditor
    },
    mixins: [toastable, utils],
    data: () => {
        return {
            documentWizardRenderer: null,
            showModal: false,
            documents: [],
            selectedDocument: null,
            loading: false,
            currentPage: 1,
            perPage: 10,
            total: null,
            lastPage: 1,
            baseURL: '',
            editor: null,
            generatedDocument: null,
            showGeneratedDocumentEditor : false
        };
    },
    mounted() {
        this.baseURL = this.$root.config.VUE_APP_BASE_URI + this.$root.config.VUE_APP_BASE_PATH;
        // Estensione OrderedList con supporto per attributo 'type' (es. type="a")
        const OrderedListWithType = OrderedList.extend({
            addAttributes() {
                const parent = typeof this.parent === 'function' ? this.parent() : {};
                return {
                    ...parent,
                    type: {
                        default: null,
                        parseHTML: element => element.getAttribute('type') || null,
                        renderHTML: attributes => (attributes.type ? { type: attributes.type } : {})
                    }
                };
            }
        });

        this.editor = new Editor({
            content: this.item?.content || "",
            extensions: [
                StarterKit,
                Heading,
                BulletList,
                OrderedListWithType,
                ListItem,
                Bold,
                Italic,
                Link,
                TextAlign.configure({
                    types: ['heading', 'paragraph'],
                    alignments: ['left', 'center', 'right', 'justify']
                }),
                Table.configure({
                    resizable: true,
                    HTMLAttributes: { class: 'table table-bordered align-middle w-auto' }
                }),
                TableRow,
                TableHeader,
                TableCell,
                ImageResize.configure({
                    inline: false,
                    allowBase64: true,
                    HTMLAttributes: {
                        class: 'resizable-image',
                    }
                })
            ],
            parseOptions: {
                preserveWhitespace: true
            }
        });
    },
    methods: {
        paginationItems() {
            const last = Number(this.lastPage || 1);
            const current = Number(this.currentPage || 1);

            if (last <= 7) {
                return Array.from({ length: last }, (_, i) => i + 1);
            }

            const items = [];
            const push = (v) => items.push(v);

            push(1);

            const left = Math.max(2, current - 1);
            const right = Math.min(last - 1, current + 1);

            if (left > 2) push('...');

            for (let p = left; p <= right; p++) {
                push(p);
            }

            if (right < last - 1) push('...');

            push(last);
            return items;
        },
        async fetchDocuments(page = 1) {
            this.loading = true;
            try {
                const url = new URL(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/documents`);
                url.searchParams.set('page', String(page));
                url.searchParams.set('per_page', String(this.perPage));

                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    this.documents = data.data || data;
                    this.currentPage = Number(data.current_page || page || 1);
                    this.perPage = Number(data.per_page || this.perPage);
                    this.total = data.total !== undefined ? Number(data.total) : null;
                    this.lastPage = Number(data.last_page || 1);
                } else {
                    this.showErrorToast('Errore', 'Si è verificato un errore nella ricerca dei documenti');
                }
            } catch (error) {
                this.showErrorToast('Errore', 'Si è verificato un errore nella richiesta');
            } finally {
                this.loading = false;
            }
        },
        async goToPage(page) {
            const nextPage = Math.max(1, Math.min(Number(page || 1), this.lastPage || 1));
            if (nextPage === this.currentPage) return;
            await this.fetchDocuments(nextPage);
        },
        downloadOdt() {
            fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/documents/to-file`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    type: 'odt',
                    content: this.$refs.richTextEditor.editor.getHTML()
                })
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error('Risposta non valida: contenuto mancante');
                }
                const data = await response.json();
                const { content, mime, filename } = data;
                // Decodifica base64 -> Blob
                const byteChars = atob(content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                    byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: mime || 'application/vnd.oasis.opendocument.text' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            });
        },
        downloadDocx() {
            fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/documents/to-file`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    type: 'docx',
                    content: this.$refs.richTextEditor.editor.getHTML()
                })
            }).then(async (response) => {
                if (!response.ok) {
                    throw new Error('Risposta non valida: contenuto mancante');
                }
                const { content, mime, filename } = await response.json();
                // Decodifica base64 -> Blob
                const byteChars = atob(content);
                const byteNumbers = new Array(byteChars.length);
                for (let i = 0; i < byteChars.length; i++) {
                    byteNumbers[i] = byteChars.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: mime || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            });
        },
        async compileDocumentPost(endpoint, wizard) {
            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(wizard)
                });
                return { "data" : await response.json()}
            } catch (error) {
                this.showErrorToast('Errore', 'Si è verificato un errore nella generazione del documento');
            }
        },  
        async openDocumentComposition() {
            this.showModal = true;
            this.currentPage = 1;
            this.total = null;
            this.lastPage = 1;
            await this.fetchDocuments(1);
        },
        closeModal() {
            this.showModal = false;
            this.documents = [];
            this.currentPage = 1;
            this.total = null;
            this.lastPage = 1;
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('it-IT', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        showEditor(response) {
            // Open the editor modal first
            this.selectedDocument = null
            this.showGeneratedDocumentEditor = true;
            // Guard ref access in case it is not available yet
            if (this.$refs.documentWizardRenderer) {
                this.$refs.documentWizardRenderer.currentState = 'wizard';
            }
            // Set content only if the editor exists
            if (this.editor) {
                this.editor.commands.setContent(response.data.content);
            }
        },
        async selectDocument(document) {
            // Emetti un evento per notificare il componente padre
            try {
                const response = await fetch(`${this.$root.config.VUE_APP_BACKOFFICE_URL}/documents/${document.id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    this.selectedDocument = data;
                    // Chiudi il modale della lista e apri il wizard
                    this.closeModal();
                } else {
                    console.error('Errore nel caricamento del documento:', response.status);
                }
            } catch (error) {
                console.error('Errore nella richiesta:', error);
            }
        },
        closeWizardModal() {
            this.selectedDocument = null;
        }
    }
}
</script>

<style>
.document-item {
    transition: background-color 0.2s ease;
    border-left: 3px solid transparent;
    padding: 1rem 1.25rem;
}

.document-item:hover {
    background-color: rgba(0, 102, 204, 0.05);
    border-left-color: #0066CC;
}

.list-group-flush .list-group-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.list-group-flush .list-group-item:last-child {
    border-bottom: none;
}

.icon-xs {
    width: 0.875rem;
    height: 0.875rem;
}

.gap-3 {
    gap: 1rem;
}

.floating-download-buttons {
    position: fixed;
    top: 16px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1060; /* above Bootstrap modal (1055) */
}

</style>